import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Search, ExternalLink, Loader2, Users, ArrowUpDown,
  Twitter, Youtube, AlertCircle, Globe
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_CONFIG = {
  all: { label: 'All', icon: Globe, color: '#666' },
  twitter: { label: 'X (Twitter)', icon: Twitter, color: '#1DA1F2' },
  youtube: { label: 'YouTube', icon: Youtube, color: '#FF0000' },
  tiktok: { label: 'TikTok', icon: Globe, color: '#010101' },
};

const SORT_OPTIONS = [
  { key: 'tips', label: 'Most Tipped' },
  { key: 'followers', label: 'Most Followers' },
  { key: 'name', label: 'A-Z' },
];

const formatNumber = (n) => {
  if (n == null) return '--';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const formatTipAmount = (n) => {
  if (n == null || n === 0) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(1);
};

const PlatformIcon = ({ platform, size = 16 }) => {
  const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.all;
  const Icon = config.icon;
  return <Icon size={size} style={{ color: config.color }} />;
};

const CreatorRow = ({ creator }) => (
  <a
    href={creator.profileUrl || '#'}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 p-3 rounded-lg bg-[#f5f3f0] hover:bg-[#ece8df] transition-colors group"
    data-testid={`creator-row-${creator.platform}-${creator.handle}`}
  >
    {/* Avatar placeholder */}
    <div className="w-10 h-10 rounded-full bg-[#d4cfc4] flex items-center justify-center flex-shrink-0 text-sm font-bold text-[#888] uppercase overflow-hidden">
      {creator.avatarUrl
        ? <img src={creator.avatarUrl} alt="" className="w-full h-full object-cover" />
        : (creator.displayName?.charAt(0) || creator.handle?.charAt(0) || '?')
      }
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-[#1a1a1a] text-sm truncate">
          {creator.displayName || creator.handle}
        </span>
        <PlatformIcon platform={creator.platform} size={14} />
      </div>
      <span className="text-xs text-[#888] truncate block">
        @{creator.handle}
      </span>
    </div>

    {/* Stats */}
    <div className="flex items-center gap-4 flex-shrink-0 text-xs text-[#888]">
      {creator.followerCount != null && (
        <div className="text-right hidden sm:block">
          <div className="font-medium text-[#555]">{formatNumber(creator.followerCount)}</div>
          <div>followers</div>
        </div>
      )}
      <div className="text-right">
        <div className="font-medium text-[#555]">{formatTipAmount(creator.totalTipAmount)} TIP</div>
        <div>{creator.tipTransactionCount} tip{creator.tipTransactionCount !== 1 ? 's' : ''}</div>
      </div>
      <ExternalLink size={14} className="text-[#b8b3ab] group-hover:text-[#f0b90b] transition-colors flex-shrink-0" />
    </div>
  </a>
);

export default function DiscoverPage({ embed = false }) {
  const [creators, setCreators] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activePlatform, setActivePlatform] = useState('all');
  const [sortBy, setSortBy] = useState('tips');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCreators = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ sort: sortBy, limit: '500' });
      if (activePlatform !== 'all') params.set('platform', activePlatform);

      const res = await axios.get(`${BACKEND_URL}/api/discover/creators?${params}`);
      setCreators(res.data.creators || []);
      setPlatforms(res.data.platforms || {});
      setTotal(res.data.total || 0);
    } catch (err) {
      setError('Failed to load creators. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activePlatform, sortBy]);

  useEffect(() => { fetchCreators(); }, [fetchCreators]);

  // Client-side search filter
  const filtered = searchQuery.trim()
    ? creators.filter(c =>
        c.handle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.displayName || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : creators;

  // Available platform tabs from data
  const availablePlatforms = ['all', ...Object.keys(platforms).sort()];

  return (
    <div className={embed ? 'p-4' : 'min-h-screen bg-[#ccc8bc]'}>
      <div className={embed ? '' : 'max-w-4xl mx-auto px-4 py-8'}>

        {/* Controls */}
        <Card className="p-4 bg-white border-[#b8b3ab] mb-4" data-testid="discover-controls">

          {/* Platform Tabs */}
          <div className="flex gap-1 mb-3 overflow-x-auto pb-1" data-testid="platform-tabs">
            {availablePlatforms.map((p) => {
              const config = PLATFORM_CONFIG[p] || { label: p, icon: Globe, color: '#666' };
              const Icon = config.icon;
              const count = p === 'all' ? total : (platforms[p] || 0);
              const isActive = activePlatform === p;

              return (
                <button
                  key={p}
                  onClick={() => setActivePlatform(p)}
                  data-testid={`platform-tab-${p}`}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                    whitespace-nowrap transition-colors
                    ${isActive
                      ? 'bg-[#1a1a1a] text-white'
                      : 'bg-[#f5f3f0] text-[#666] hover:bg-[#ece8df]'}
                  `}
                >
                  <Icon size={13} />
                  {config.label}
                  <span className={`${isActive ? 'text-[#999]' : 'text-[#aaa]'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search + Sort */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#999]" />
              <Input
                placeholder="Search by name or handle..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 bg-[#f5f3f0] border-[#d4cfc4] text-sm h-9"
                data-testid="discover-search"
              />
            </div>
            <div className="flex gap-1">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  data-testid={`sort-${opt.key}`}
                  className={`
                    px-2.5 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap
                    ${sortBy === opt.key
                      ? 'bg-[#f0b90b] text-white'
                      : 'bg-[#f5f3f0] text-[#666] hover:bg-[#ece8df]'}
                  `}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Results */}
        <Card className="bg-white border-[#b8b3ab] overflow-hidden" data-testid="discover-results">

          {/* Count bar */}
          <div className="px-4 py-2.5 border-b border-[#ece8df] flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-[#888]">
              <Users size={13} />
              <span>{filtered.length} creator{filtered.length !== 1 ? 's' : ''}</span>
              {searchQuery && <span className="text-[#bbb]">matching "{searchQuery}"</span>}
            </div>
            <div className="flex items-center gap-1 text-xs text-[#bbb]">
              <ArrowUpDown size={11} />
              {SORT_OPTIONS.find(s => s.key === sortBy)?.label}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16 text-[#888]" data-testid="discover-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading creators...
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="p-4" data-testid="discover-error">
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <AlertDescription className="text-red-700 text-sm">{error}</AlertDescription>
              </Alert>
              <div className="text-center mt-3">
                <Button onClick={fetchCreators} variant="outline" size="sm" className="text-xs">
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-16 text-[#888]" data-testid="discover-empty">
              <Users className="w-8 h-8 mx-auto mb-2 text-[#ccc]" />
              <p className="text-sm font-medium">No creators found</p>
              <p className="text-xs mt-1">
                {searchQuery ? 'Try a different search term.' : 'No verified creators on this platform yet.'}
              </p>
            </div>
          )}

          {/* Creator list */}
          {!loading && !error && filtered.length > 0 && (
            <div className="p-3 space-y-1.5" data-testid="discover-list">
              {filtered.map((creator, i) => (
                <CreatorRow key={`${creator.platform}-${creator.handle}-${i}`} creator={creator} />
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
